import React, { createContext, useContext, useState, useCallback } from 'react';
import { translations, translate } from '../utils/translate';

const LanguageContext = createContext();

const LANGUAGE_COOKIE_NAME = 'app_language';

// Helper functions for cookie management
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Helper function to detect browser language
  const detectBrowserLanguage = () => {
    const supportedLanguages = ['en', 'sr', 'hr', 'ro', 'hu', 'cs', 'sk', 'uk'];
    
    // Get browser languages (array of preferred languages)
    const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];
    
    // Try to find a match
    for (const browserLang of browserLanguages) {
      // Extract language code (e.g., 'en' from 'en-US', 'uk' from 'uk-UA')
      const langCode = browserLang.toLowerCase().split('-')[0];
      
      // Check if we support this language
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }
    
    // Fallback to English if no supported language found
    return 'en';
  };

  // Initialize language from cookie, browser detection, or default to English
  const [language, setLanguageState] = useState(() => {
    const savedLanguage = getCookie(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && translations[savedLanguage]) {
      return savedLanguage;
    }
    // No saved language, detect from browser
    return detectBrowserLanguage();
  });

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      setCookie(LANGUAGE_COOKIE_NAME, lang);
    }
  }, []);

  // Get translation by key path (e.g., "common.add")
  const t = useCallback((key, defaultValue = key, params = {}) => {
    return translate(language, key, defaultValue, params);
  }, [language]);

  // Get all translations for a section (e.g., "common")
  const getSection = useCallback((section) => {
    return translations[language][section] || {};
  }, [language]);

  // Helper function to get localized month name
  const getMonthName = useCallback((monthIndex, short = false) => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    const monthShortKeys = [
      'jan', 'feb', 'mar', 'apr', 'may', 'jun',
      'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
    ];
    
    const key = short ? monthShortKeys[monthIndex] : monthKeys[monthIndex];
    const section = short ? 'calendar.monthsShort' : 'calendar.months';
    return t(`${section}.${key}`);
  }, [t]);

  const formatWeddingDate = (dateString, short = false) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = getMonthName(date.getMonth(), true);
    const year = date.getFullYear();

    if (short)
      return `${day} ${month}`;
    else
      return `${day} ${month} ${year}`;
  };

  // Helper function to get localized day name
  const getDayName = useCallback((dayIndex, short = false) => {
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayShortKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    
    const key = short ? dayShortKeys[dayIndex] : dayKeys[dayIndex];
    const section = short ? 'calendar.daysShort' : 'calendar.days';
    return t(`${section}.${key}`);
  }, [t]);

  // Helper function to format date with localized month/day names
  const formatDate = useCallback((date, options = {}) => {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    const { 
      includeWeekday = false, 
      includeYear = false, 
      shortMonth = false,
      shortWeekday = false 
    } = options;
    
    let formatted = '';
    
    if (includeWeekday) {
      formatted += getDayName(dateObj.getDay(), shortWeekday);
      formatted += ', ';
    }
    
    formatted += getMonthName(dateObj.getMonth(), shortMonth);
    formatted += ' ' + dateObj.getDate();
    
    if (includeYear) {
      formatted += ', ' + dateObj.getFullYear();
    }
    
    return formatted;
  }, [getMonthName, getDayName]);

  // Helper function to translate backend messages
  const translateMessage = useCallback((message) => {
    if (!message) return message;
    
    // Map of backend messages to translation keys
    const messageMap = {
      // Success messages
      'Salon created successfully': 'messages.success.salonCreated',
      'User created successfully. Please check your email to verify your account.': 'messages.success.userCreated',
      'Email verified successfully': 'messages.success.emailVerified',
      'Password reset successfully': 'messages.success.passwordReset',
      'Reservation created successfully': 'messages.success.reservationCreated',
      'Reservation created successfully (email notification failed)': 'messages.success.reservationCreatedEmailFailed',
      'Reservation updated successfully': 'messages.success.reservationUpdated',
      'Reservation deleted successfully': 'messages.success.reservationDeleted',
      'Reservation restored successfully': 'messages.success.reservationRestored',
      'Dress created successfully': 'messages.success.dressCreated',
      'Dress updated successfully': 'messages.success.dressUpdated',
      'Dress deleted successfully': 'messages.success.dressDeleted',
      'Dress permanently deleted successfully': 'messages.success.dressPermanentlyDeleted',
      'Image deleted successfully': 'messages.success.imageDeleted',
      'Appointment created successfully': 'messages.success.appointmentCreated',
      'Appointment updated successfully': 'messages.success.appointmentUpdated',
      'Appointment deleted successfully': 'messages.success.appointmentDeleted',
      'Accessory created successfully': 'messages.success.accessoryCreated',
      'Accessory updated successfully': 'messages.success.accessoryUpdated',
      'Accessory deleted successfully': 'messages.success.accessoryDeleted',
      'Accessory restored successfully': 'messages.success.accessoryRestored',
      'Dress brand created successfully': 'messages.success.brandCreated',
      'Dress brand updated successfully': 'messages.success.brandUpdated',
      'Dress brand deleted successfully': 'messages.success.brandDeleted',
      'Contract template created successfully': 'messages.success.contractCreated',
      'Contract created successfully': 'messages.success.contractCreated',
      'Contract updated successfully': 'messages.success.contractUpdated',
      'Contract deleted successfully': 'messages.success.contractDeleted',
      'Successfully joined salon': 'messages.success.joinedSalon',
      'Member added successfully': 'messages.success.memberAdded',
      'Member role updated successfully': 'messages.success.memberRoleUpdated',
      'Member removed from salon successfully': 'messages.success.memberRemoved',
      'Salon marked for deletion successfully': 'messages.success.salonDeleted',
      'Setting updated successfully': 'messages.success.settingUpdated',
      'Setting created successfully': 'messages.success.settingCreated',
      'Setting deleted successfully': 'messages.success.settingDeleted',
      'Settings updated successfully': 'messages.success.settingsUpdated',
      'A new verification link has been sent to your email.': 'messages.success.verificationEmailSent',
      'Password reset email sent': 'messages.success.passwordResetEmailSent',
      'Login successful': 'messages.success.loginSuccessful',
      // Error messages
      'Login failed': 'messages.error.loginFailed',
      'Registration failed': 'messages.error.registrationFailed',
      'Password reset request failed': 'messages.error.passwordResetRequestFailed',
      'Password reset failed': 'messages.error.passwordResetFailed',
      'Email verification failed': 'messages.error.emailVerificationFailed',
      'Failed to resend verification email': 'messages.error.resendVerificationFailed',
      'User already exists': 'messages.error.userAlreadyExists',
      'Invalid credentials': 'messages.error.invalidCredentials',
      'Please verify your email before logging in': 'messages.error.verifyEmailBeforeLogin',
      'Account is deactivated': 'messages.error.accountDeactivated',
      'Email is already verified. You can log in.': 'messages.error.emailAlreadyVerified',
      'Database error': 'messages.error.databaseError',
      'Server error': 'messages.error.serverError',
      'Failed to create user': 'messages.error.failedToCreateUser',
      'Verification token required': 'messages.error.verificationTokenRequired',
      'Invalid or expired verification token': 'messages.error.invalidOrExpiredToken',
      'Failed to verify email': 'messages.error.failedToVerifyEmail',
      'Failed to generate new verification token': 'messages.error.failedToGenerateToken',
      'User not found': 'messages.error.userNotFound',
      'Failed to generate reset token': 'messages.error.failedToGenerateResetToken',
      'Invalid or expired reset token': 'messages.error.invalidOrExpiredResetToken',
      'If an account exists with this email, a new verification link has been sent.': 'messages.error.verificationEmailMayBeSent'
    };
    
    // Check if message matches any known backend message
    const translationKey = messageMap[message];
    if (translationKey) {
      return t(translationKey);
    }
    
    // If no exact match, return original message
    return message;
  }, [t]);

  const value = {
    language,
    setLanguage,
    t,
    getSection,
    getMonthName,
    formatWeddingDate,
    getDayName,
    formatDate,
    translateMessage,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
      { code: 'sr', name: 'Serbian', nativeName: 'Српски', flag: '🇷🇸' },
      { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷' },
      { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
      { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺' },
      { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
      { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰' },
      { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

